/**
 * LeadsPage — composer for the Leads listing page.
 *
 * Originally 579 lines (Iter 145). Split in Iter 156.10 (Feb 28, 2026):
 *   • `leads/useLeads.js`           — state + handlers (custom hook)
 *   • `leads/LeadsFilters.jsx`      — search + filter chips
 *   • `leads/LeadsTable.jsx`        — table + pagination
 *   • `leads/NewLeadDialog.jsx`     — modal
 *   • (this file)                   — page header + composition
 */
import { Loader2, Plus, RefreshCw, Download } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import DateRangePicker from "../components/DateRangePicker";

import useLeads from "./leads/useLeads";
import LeadsFilters from "./leads/LeadsFilters";
import LeadsTable from "./leads/LeadsTable";
import NewLeadDialog from "./leads/NewLeadDialog";

const STATUSES = [
  { value: "new", label: "New" },
  { value: "open", label: "Open" },
  { value: "pending", label: "Pending" },
  { value: "closed", label: "Closed" },
  { value: "solved", label: "Solved" },
];

const CATEGORIES = [
  "Books", "Paintings", "Sculptures", "Jewelry", "Sarees",
  "Hindu Statues", "Buddhist Statues", "Tibetan",
];

const STATUS_LABELS = {
  new: "New", open: "Open", pending: "Pending",
  closed: "Closed", solved: "Solved",
};

const LeadsPage = () => {
  const { user } = useAuth();
  const h = useLeads(user);
  const canExport = user?.role === "admin" || user?.role === "super_admin" || user?.role === "leader";
  const canSync = user?.role === "admin" || user?.role === "super_admin";

  return (
    <div data-testid="leads-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 animate-fade-up">
        <div>
          <h1 className="text-2xl lg:text-3xl font-semibold text-[--text-primary]" style={{ fontFamily: "Playfair Display, serif" }}>
            Leads
          </h1>
          <p className="text-sm text-[--text-muted] mt-1">
            {h.total} {h.total === 1 ? "lead" : "leads"} total
          </p>
        </div>
        <div className="flex items-center gap-3">
          <DateRangePicker
            dateRange={h.dateRange}
            onDateRangeChange={(range) => { h.setDateRange(range); h.setPage(1); }}
          />
          {canSync && (
            <button
              onClick={h.handleSyncWati}
              disabled={h.syncing}
              className="btn-secondary flex items-center gap-2"
              data-testid="sync-wati-btn"
            >
              {h.syncing ? <Loader2 size={18} className="animate-spin" /> : <RefreshCw size={18} />}
              {h.syncing ? "Syncing..." : "Sync WATI"}
            </button>
          )}
          {canExport && (
            <button
              onClick={h.handleExport}
              className="btn-secondary flex items-center gap-2"
              data-testid="export-leads-btn"
            >
              <Download size={18} /> Export
            </button>
          )}
          <button
            onClick={() => h.setShowNewLead(true)}
            className="btn-primary flex items-center gap-2"
            data-testid="add-lead-btn"
          >
            <Plus size={18} /> Add Lead
          </button>
        </div>
      </div>

      <LeadsFilters
        search={h.search} setSearch={h.setSearch} setPage={h.setPage}
        showFilters={h.showFilters} setShowFilters={h.setShowFilters}
        hasFilters={h.hasFilters} clearFilters={h.clearFilters}
        statusFilter={h.statusFilter} setStatusFilter={h.setStatusFilter}
        categoryFilter={h.categoryFilter} setCategoryFilter={h.setCategoryFilter}
        assignedFilter={h.assignedFilter} setAssignedFilter={h.setAssignedFilter}
        salespeople={h.salespeople}
        STATUSES={STATUSES} CATEGORIES={CATEGORIES} user={user}
      />

      <LeadsTable
        loading={h.loading} leads={h.leads}
        hasFilters={h.hasFilters} STATUS_LABELS={STATUS_LABELS}
        page={h.page} pages={h.pages} setPage={h.setPage}
      />

      <NewLeadDialog
        showNewLead={h.showNewLead} setShowNewLead={h.setShowNewLead}
        newLeadData={h.newLeadData} setNewLeadData={h.setNewLeadData}
        handleCreateLead={h.handleCreateLead}
      />
    </div>
  );
};

export default LeadsPage;
